/**
 * PDF Viewer Component
 * Enhanced PDF preview using PDF.js via react-pdf library
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  Paper, 
  Box, 
  Typography, 
  CircularProgress, 
  Alert,
  IconButton,
  Button,
  ButtonGroup,
  Paper as CalloutPaper
} from '@mui/material';
import { 
  Description as PdfIcon,
  ZoomIn,
  ZoomOut,
  NavigateBefore,
  NavigateNext,
  Fullscreen,
  FullscreenExit,
  Close as CloseIcon
} from '@mui/icons-material';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import { BOMItem } from '../types/bom.types';

// Configure PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

interface PDFViewerProps {
  pdfUrl: string;
  isExpanded?: boolean;
  onToggleExpand?: () => void;
  currentPage?: number;
  selectedItem?: BOMItem | null;
  onClose?: () => void;
}

const PDFViewer: React.FC<PDFViewerProps> = ({ pdfUrl, isExpanded = false, onToggleExpand, currentPage, selectedItem, onClose }) => {
  const [numPages, setNumPages] = useState<number | null>(null);
  const [pageNumber, setPageNumber] = useState(currentPage || 1);
  const [scale, setScale] = useState(1.2); // Default zoom 120% to match Training view
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [calloutPosition, setCalloutPosition] = useState<{ x: number; y: number; arrowSide?: 'left' | 'right' } | null>(null);
  const [showCallout, setShowCallout] = useState(false);
  const pdfContainerRef = useRef<HTMLDivElement>(null);
  const lastSelectedRef = useRef<string | null>(null);
  const targetElementRef = useRef<HTMLElement | null>(null); // Store target element for position updates

  // Reset state when PDF URL changes
  useEffect(() => {
    setNumPages(null);
    setPageNumber(currentPage || 1);
    setLoading(true);
    setError(null);
  }, [pdfUrl, currentPage]);
  
  // Update page number when currentPage prop changes
  useEffect(() => {
    if (currentPage && currentPage > 0 && currentPage <= (numPages || Infinity)) {
      setPageNumber(currentPage);
    }
  }, [currentPage, numPages]);
  
  // Calculate callout position relative to container (accounting for PDF zoom/scale)
  const calculateCalloutPosition = useCallback((element: HTMLElement, container: HTMLElement, pdfScale: number) => {
    const elementRect = element.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();
    
    // IMPORTANT: getBoundingClientRect returns scaled coordinates (affected by PDF zoom)
    // But scrollLeft/scrollTop are in unscaled document coordinates
    // So we need to convert: divide by scale to get unscaled position, then add scroll
    const relativeLeft = (elementRect.left - containerRect.left) / pdfScale + container.scrollLeft;
    const relativeTop = (elementRect.top - containerRect.top) / pdfScale + container.scrollTop;
    const elementWidth = elementRect.width / pdfScale;
    const elementHeight = elementRect.height / pdfScale;
    
    const calloutWidth = 250;
    const spacing = 15;
    
    // Determine if element is on right or left side of container (use viewport position for this)
    const containerCenterX = containerRect.width / 2;
    const elementCenterInContainer = elementRect.left - containerRect.left;
    const isOnRightSide = elementCenterInContainer > containerCenterX;
    
    let x: number;
    let arrowSide: 'left' | 'right';
    
    if (isOnRightSide) {
      // Place callout to the left of the element
      x = relativeLeft - calloutWidth - spacing;
      arrowSide = 'right';
    } else {
      // Place callout to the right of the element
      x = relativeLeft + elementWidth + spacing;
      arrowSide = 'left';
    }
    
    // Vertical position - center vertically with element
    const y = relativeTop + (elementHeight / 2) - 40;
    
    // Ensure callout stays within container bounds
    const maxX = container.scrollWidth - calloutWidth - 10;
    const calloutX = Math.max(10, Math.min(x, maxX));
    const calloutY = Math.max(10, y);
    
    return { x: calloutX, y: calloutY, arrowSide };
  }, []);

  // Find text element in the PDF
  const findTextElement = useCallback((targetPage: number, searchText: string): HTMLElement | null => {
    const textLayer = document.querySelector(
      `.react-pdf__Page[data-page-number="${targetPage}"] .react-pdf__Page__textContent`
    );
    
    if (!textLayer) return null;
    
    const textElements = textLayer.querySelectorAll('span');
    for (const span of Array.from(textElements)) {
      const text = span.textContent || '';
      if (text.includes(searchText) || searchText.includes(text.trim())) {
        return span as HTMLElement;
      }
    }
    return null;
  }, []);

  // Perform the actual zoom and highlight
  const performZoom = useCallback((targetPage: number, item: BOMItem) => {
    // Clear previous state
    if (targetElementRef.current) {
      const prev = targetElementRef.current;
      prev.style.backgroundColor = '';
      prev.style.boxShadow = '';
      prev.style.borderRadius = '';
      prev.style.padding = '';
      prev.style.margin = '';
      prev.style.position = '';
      prev.style.zIndex = '';
    }
    targetElementRef.current = null;
    setShowCallout(false);
    
    // Set zoom immediately
    setScale(1.2);
    
    // Try to find the element (with retries if page not ready)
    const searchText = item.reference || '';
    let attempts = 0;
    const maxAttempts = 10;
    
    const tryFind = () => {
      const targetElement = findTextElement(targetPage, searchText);
      
      if (targetElement) {
        // Found it! Store reference
        targetElementRef.current = targetElement;
        
        // Highlight the element
        targetElement.style.backgroundColor = 'rgba(255, 193, 7, 0.3)';
        targetElement.style.boxShadow = 'inset 0 0 0 2px rgba(255, 193, 7, 0.9), 0 0 8px rgba(255, 193, 7, 0.5)';
        targetElement.style.borderRadius = '2px';
        targetElement.style.padding = '1px 2px';
        targetElement.style.margin = '-1px -2px';
        targetElement.style.position = 'relative';
        targetElement.style.zIndex = '10';
        
        // Scroll into view
        targetElement.scrollIntoView({
          behavior: 'auto', // Changed to auto for instant scroll
          block: 'center',
          inline: 'center'
        });
        
        // Calculate and show callout immediately
        const container = pdfContainerRef.current;
        if (container) {
          const position = calculateCalloutPosition(targetElement, container, 1.2);
          setCalloutPosition(position);
          setShowCallout(true);
          
          // Pulse animation
          let pulseCount = 0;
          const pulseInterval = setInterval(() => {
            if (targetElementRef.current && pulseCount < 10) {
              targetElementRef.current.style.boxShadow = pulseCount % 2 === 0
                ? 'inset 0 0 0 2px rgba(255, 193, 7, 0.9), 0 0 12px rgba(255, 193, 7, 0.7)'
                : 'inset 0 0 0 2px rgba(255, 193, 7, 0.9), 0 0 8px rgba(255, 193, 7, 0.5)';
              pulseCount++;
            } else {
              clearInterval(pulseInterval);
            }
          }, 200);
        }
      } else if (attempts < maxAttempts) {
        // Not found yet, retry (page might still be rendering)
        attempts++;
        setTimeout(tryFind, 100);
      }
    };
    
    // Start searching
    tryFind();
  }, [calculateCalloutPosition, findTextElement]);
  
  const zoomToComponent = useCallback((item: BOMItem) => {
    if (!item.reference) return;
    
    const targetPage = item.page || (item.pages && item.pages[0]) || 1;
    
    if (targetPage !== pageNumber) {
      setPageNumber(targetPage);
      setTimeout(() => {
        performZoom(targetPage, item);
      }, 500);
    } else {
      performZoom(targetPage, item);
    }
  }, [pageNumber, performZoom]);
  
  useEffect(() => {
    if (selectedItem && selectedItem.reference && selectedItem.reference !== lastSelectedRef.current) {
      lastSelectedRef.current = selectedItem.reference;
      setShowCallout(false);
      zoomToComponent(selectedItem);
    } else if (!selectedItem) {
      setShowCallout(false);
      lastSelectedRef.current = null;
      // Clean up target element styling
      if (targetElementRef.current) {
        const prev = targetElementRef.current;
        prev.style.backgroundColor = '';
        prev.style.boxShadow = '';
        prev.style.borderRadius = '';
        prev.style.padding = '';
        prev.style.margin = '';
        prev.style.position = '';
        prev.style.zIndex = '';
      }
      targetElementRef.current = null;
    }
  }, [selectedItem, zoomToComponent]);

  const getApiBase = () => {
    const apiBase = process.env.REACT_APP_API_BASE;
    if (apiBase) return apiBase;
    if (window.location.hostname === 'localhost' && window.location.port === '3000') {
      return 'http://localhost:8000';
    }
    return window.location.origin;
  };
  
  const fullPdfUrl = pdfUrl.startsWith('http') 
    ? pdfUrl 
    : `${getApiBase()}${pdfUrl}`;

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
    setLoading(false);
    setError(null);
  };

  const onDocumentLoadError = (error: Error) => {
    console.error('[PDFViewer] Error loading PDF:', error);
    setError(`Failed to load PDF: ${error.message}`);
    setLoading(false);
  };

  const changePage = (offset: number) => {
    setPageNumber((prevPageNumber) => {
      const newPage = prevPageNumber + offset;
      return Math.min(Math.max(1, newPage), numPages || 1);
    });
  };

  const zoomIn = () => {
    setScale(prev => Math.min(prev + 0.2, 3.0));
  };

  const zoomOut = () => {
    setScale(prev => Math.max(prev - 0.2, 0.5));
  };

  return (
    <Paper sx={{ mt: 2, overflow: 'hidden' }}>
      <Box 
        sx={{ 
          p: 2, 
          borderBottom: 1, 
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          backgroundColor: 'background.default',
          flexWrap: 'wrap'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexGrow: 1 }}>
          <PdfIcon color="error" />
          <Typography variant="h6">PDF Preview</Typography>
        </Box>

        {loading && <CircularProgress size={20} />}

        {onClose && (
          <IconButton
            size="small"
            onClick={onClose}
            title="Close PDF"
            sx={{ ml: 'auto' }}
          >
            <CloseIcon />
          </IconButton>
        )}

        {!error && numPages && (
          <ButtonGroup size="small" variant="outlined">
            <Button onClick={zoomOut} disabled={scale <= 0.5}>
              <ZoomOut fontSize="small" />
            </Button>
            <Button disabled sx={{ minWidth: 60 }}>
              {Math.round(scale * 100)}%
            </Button>
            <Button onClick={zoomIn} disabled={scale >= 3.0}>
              <ZoomIn fontSize="small" />
            </Button>
          </ButtonGroup>
        )}

        {!loading && !error && numPages && (
          <>
            {onToggleExpand && (
              <IconButton 
                size="small" 
                onClick={onToggleExpand}
                title={isExpanded ? 'Show side-by-side' : 'Expand to full width'}
              >
                {isExpanded ? <FullscreenExit /> : <Fullscreen />}
              </IconButton>
            )}

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconButton 
                size="small" 
                onClick={() => changePage(-1)} 
                disabled={pageNumber <= 1}
              >
                <NavigateBefore />
              </IconButton>
              <Typography variant="body2" sx={{ minWidth: 100, textAlign: 'center' }}>
                Page {pageNumber} of {numPages}
              </Typography>
              <IconButton 
                size="small" 
                onClick={() => changePage(1)} 
                disabled={pageNumber >= numPages}
              >
                <NavigateNext />
              </IconButton>
            </Box>
          </>
        )}
      </Box>

      {error && (
        <Box sx={{ p: 2 }}>
          <Alert severity="error">{error}</Alert>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            URL: {fullPdfUrl}
          </Typography>
        </Box>
      )}

      <Box 
        ref={pdfContainerRef}
        sx={{ 
          position: 'relative',
          display: 'flex', 
          justifyContent: 'flex-start',
          alignItems: 'flex-start',
          backgroundColor: '#525659',
          minHeight: 600,
          p: 2,
          overflow: 'auto',
          width: '100%',
          '& > *': {
            minWidth: 'fit-content'
          }
        }}
      >
        {/* Callout positioned absolutely within container */}
        {showCallout && calloutPosition && selectedItem && (
          <CalloutPaper
            sx={{
              position: 'absolute', // Position within container, not viewport
              left: `${calloutPosition.x}px`,
              top: `${calloutPosition.y}px`,
              p: 1.5,
              backgroundColor: 'rgba(255, 255, 255, 0.95)',
              boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
              borderRadius: 2,
              zIndex: 1000, // Above PDF content
              minWidth: 200,
              maxWidth: 300,
              pointerEvents: 'auto',
              '&::after': {
                content: '""',
                position: 'absolute',
                top: '50%',
                ...(calloutPosition.arrowSide === 'right' ? {
                  left: '100%',
                  transform: 'translateY(-50%) translateX(0)',
                  borderTop: '8px solid transparent',
                  borderBottom: '8px solid transparent',
                  borderLeft: 'none',
                  borderRight: '8px solid rgba(255, 255, 255, 0.95)',
                } : {
                  right: '100%',
                  transform: 'translateY(-50%) translateX(0)',
                  borderTop: '8px solid transparent',
                  borderBottom: '8px solid transparent',
                  borderRight: 'none',
                  borderLeft: '8px solid rgba(255, 255, 255, 0.95)',
                }),
                width: 0,
                height: 0,
              }
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
              <Typography variant="subtitle2" fontWeight="bold" color="primary">
                {selectedItem.reference}
              </Typography>
              <IconButton
                size="small"
                onClick={() => setShowCallout(false)}
                sx={{ p: 0, ml: 1 }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
            {selectedItem.description && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                {selectedItem.description.substring(0, 100)}
                {selectedItem.description.length > 100 ? '...' : ''}
              </Typography>
            )}
            {selectedItem.manufacturer_suggestion && (
              <Typography variant="caption" color="text.secondary">
                Mfr: {selectedItem.manufacturer_suggestion}
              </Typography>
            )}
            {selectedItem.part_number && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                PN: {selectedItem.part_number}
              </Typography>
            )}
          </CalloutPaper>
        )}
        {!error && (
          <Box sx={{ display: 'inline-block', minWidth: 'fit-content' }}>
            <Document
              file={fullPdfUrl}
              onLoadSuccess={onDocumentLoadSuccess}
              onLoadError={onDocumentLoadError}
              loading={
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  minHeight: 500,
                  color: 'white'
                }}>
                  <CircularProgress size={60} sx={{ color: 'white' }} />
                  <Typography sx={{ mt: 2, color: 'white' }}>
                    Loading PDF...
                  </Typography>
                </Box>
              }
              error={
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  minHeight: 500
                }}>
                  <Alert severity="error">Failed to load PDF</Alert>
                </Box>
              }
            >
              <Page 
                pageNumber={pageNumber} 
                scale={scale}
                renderTextLayer={true}
                renderAnnotationLayer={true}
              />
            </Document>
          </Box>
        )}
      </Box>
    </Paper>
  );
};

export default PDFViewer;
